<template>
  <div class="music-player">
    <div class="music-player-body">
      <div class="music-player-title">
        <MusicPlayerMarquee :text="title" />
      </div>
      <i-tag-list
        class="music-player-category"
        :tags="category"
        @click="onAudioEnded"
      />
      <div class="music-player-controls">
        <i
          class="isif"
          :class="detectMainBtnCssClass()"
          @click="onMainBtnClick"
        />
        <MusicPlayerProgress
          v-model="currentTime"
          :max="duration"
          @seek="onSeek"
          @seeked="onSeeked"
        />
        <span class="music-player-time">{{ countdown }}</span>
      </div>
    </div>
    <div class="music-player-close">
      <i
        class="isif isif-close"
        @click="stop"
      />
    </div>
    <audio
      ref="audioRef"
      :loop="loop"
      @canplay="onAudioCanPlay"
      @timeupdate="onAudioTimeUpdate"
      @playing="onAudioPlaying"
      @waiting="onAudioWaiting"
      @pause="onAudioPause"
      @ended="onAudioEnded"
    />
    <div
      v-if="disabled"
      class="music-play-disable-mask"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'
import MusicPlayerMarquee from './marquee'
import MusicPlayerProgress from './progress'
import ITagList from '@/components/ITagList'
import { judgeIfIsIphoneSafari } from '@/utils/browser'
import { silentAudio } from '@/api/constants'

const STATUS = {
  playing: 'PLAYING',
  pause: 'PAUSE',
  waiting: 'WAITING',
  ended: 'ENDED'
}

export default {
  name: 'MusicPlayer',
  components: {
    MusicPlayerMarquee,
    MusicPlayerProgress,
    ITagList
  },
  props: {
    loop: {
      type: Boolean,
      default: false
    },
    isTourring: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      current: null,
      duration: null,
      currentTime: 0,
      countdown: '00:00',
      status: null
    }
  },
  computed: {
    title () {
      return _.get(this.current, 'title')
    },
    source () {
      return _.get(this.current, 'source')
    },
    category () {
      const categoryMulti = _.get(this.current, 'categoryMulti')
      const categoryDefault = _.get(this.current, 'category')
      return categoryMulti?.[this.$i18n.locale] || categoryDefault
    }
  },
  mounted () {
    if (judgeIfIsIphoneSafari()) {
      // In iphone safari, audio is not allow to play without userEvent
      window.addEventListener('touchstart', this.allowSafariAutoPlay)
    }
  },
  methods: {
    ...mapActions([
      'setMusicPlayerRef',
      'pauseAudio',
      'pauseAllVideo',
      'realPausePilotPlaying'
    ]),
    initData () {
      Object.assign(this.$data, this.$options.data())
    },
    detectMainBtnCssClass () {
      const { status } = this
      const cssClass = []

      // set icon-font class
      let icon
      switch (status) {
        case STATUS.waiting:
          icon = 'isif-spinner-c'
          break
        case STATUS.playing:
          icon = 'isif-pause'
          break
        default:
          icon = 'isif-play'
      }
      cssClass.push(icon)

      return cssClass
    },
    play () {
      if (!this.isTourring) {
        this.pauseAudio()
        this.pauseAllVideo()
        this.realPausePilotPlaying()
      }
      const audioRef = this.$refs.audioRef
      return audioRef.play()
    },
    pause () {
      const audioRef = this.$refs.audioRef
      audioRef.pause()
    },
    toggle () {
      const { status } = this
      if (status === STATUS.playing) {
        this.pause()
      } else {
        this.play()
      }
    },
    stop () {
      this.pause()
      setTimeout(this.initData, 0)
      this.$emit('stop')
    },
    load (obj) {
      const audioRef = this.$refs.audioRef
      if (!audioRef) return

      this.initData()
      this.current = obj
      audioRef.src = this.source
      this.play()
      this.setMusicPlayerRef(this.$refs.audioRef)
    },
    setStatus (status) {
      this.status = status
    },
    setCountdown () {
      const { currentTime, duration } = this
      const time = duration - currentTime
      const min = parseInt(time / 60, 10)
      const sec = parseInt(time % 60, 10)
      const minutes = _.padStart(min, 2, '0')
      const seconds = _.padStart(sec, 2, '0')
      this.countdown = `${minutes}:${seconds}`
    },
    async seekTo (seconds) {
      const audioRef = this.$refs.audioRef
      this.pause()
      audioRef.currentTime = seconds
      await this.play()
    },
    onSeek () {
      this.pause()
    },
    onSeeked (seconds) {
      this.seekTo(seconds)
      this.$emit('onSeeked', seconds)
    },
    onMainBtnClick () {
      const { status } = this
      switch (status) {
        case STATUS.pause:
        case STATUS.stop:
          this.play()
          break
        case STATUS.playing:
          this.pause()
          break
        default:
      }
    },
    onAudioTimeUpdate () {
      if (this.status !== STATUS.playing) return

      const audioRef = this.$refs.audioRef
      this.currentTime = audioRef.currentTime
      this.setCountdown()
    },
    onAudioCanPlay () {
      const audioRef = this.$refs.audioRef
      this.duration = audioRef.duration
    },
    onAudioPlaying () {
      this.setStatus(STATUS.playing)
      this.$emit('playing')
    },
    onAudioWaiting () {
      this.setStatus(STATUS.waiting)
      this.$emit('waiting')
    },
    onAudioPause () {
      this.setStatus(STATUS.pause)
      this.$emit('pause')
    },
    onAudioEnded () {
      this.setStatus(STATUS.ended)
      this.$emit('ended')
    },
    allowSafariAutoPlay () {
      // In iphone safari, audio is not allow to play without userEvent
      this.$refs.audioRef.autoplay = true
      this.$refs.audioRef.src = silentAudio
      this.setMusicPlayerRef(this.$refs.audioRef)
      window.removeEventListener('touchstart', this.allowSafariAutoPlay)
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/addon/main';

.music-player {
  position: absolute;
  top: 1.6875rem;
  left: 15rem;
  width: 16.125rem;
  padding: 0.5rem 0.5rem 0.3rem;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 2px;
  font-size: 0.875rem;
  user-select: none;
  overflow: hidden;
  z-index: 150;

  @media (max-width: $response - 1) {
    top: 15rem;
    left: 50%;
    transform: translateX(-50%);
    padding: .5rem;
    display: flex;
    align-items: stretch;
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.8);
  }

  .music-player-body {
    width: 100%;
  }

  .music-player-title {
    display: flex;
    margin-right: 1.4rem;
  }

  .music-player-category {
    margin: 5px 0px;
  }

  .music-player-close {
    margin: 0.5rem 0.5rem 0 0;
    position: absolute;
    top: 0;
    right: 0;
    color: $dusty-gray;

    &:hover {
      color: $white;
    }
  }

  .music-player-controls {
    margin-top: 0.3rem;
    display: flex;
    align-items: center;
    color: $white;

    .music-player-time {
      display: block;
      min-width: 2.75em;
      margin-left: 0.5rem;
      flex-shrink: 0;
      text-align: right;
      overflow: hidden;
    }

    i {
      margin-right: 0.5rem;
      padding: 0.4rem;
      border-radius: 50%;

      &:hover,
      &:active {
        background-color: rgba(255, 255, 255, 0.15);
      }
    }
  }

  .isif {
    cursor: pointer;

    &.isif-spinner-c {
      animation: spin 1.4s infinite linear;
      pointer-events: none;
    }
  }

  audio {
    display: none;
  }

  .music-play-disable-mask {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    background: transparent;
    height: 4rem;
  }
}
</style>
